import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  Datastore,
  DatastoreCollection,
  DatastoreDocument,
} from '@freelancer/datastore';
import {
  FreelancerReputation,
  LocalHourlyRate,
  ProfileCategoriesCollection,
  ProfileViewUser,
  ProfileViewUsersCollection,
  TopSkillsCollection,
} from '@freelancer/datastore/collections';
import { RedirectToPhp } from '@freelancer/redirect-to-php';
import { ButtonColor } from '@freelancer/ui/button';
import { trackById } from '@freelancer/ui/helpers';
import { Margin } from '@freelancer/ui/margin';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  PortfolioItemCount,
  PortfolioItemSize,
} from '../../projects/project-view/user-profile-information/user-portfolio/user-portfolio.component';
import {
  AwardProjectHireMeState,
  AwardProjectModel,
} from '../user-profile-award-project.types';

interface PortfolioCategoryTab {
  readonly name: string;
  readonly id: number;
}

@Component({
  selector: 'app-user-profile-portfolio',
  template: `
    <fl-card [edgeToEdge]="true">
      <fl-card-header-title i18n="Portfolio heading">
        Portfolio Items
      </fl-card-header-title>

      <fl-card-header-right>
        <ng-container *ngIf="isLoggedIn$ | flAsync; else isLoggedOut">
          <ng-container *ngIf="isViewerProfileOwner$ | flAsync">
            <fl-button
              i18n="Portfolio manage button"
              flTrackingLabel="UserProfileManagePortfolio"
              [color]="ButtonColor.SECONDARY"
              (click)="handleManagePortfolioClicked()"
            >
              Manage
            </fl-button>
          </ng-container>
        </ng-container>

        <ng-template #isLoggedOut>
          <app-user-profile-logged-out-link
            flTrackingLabel="LoggedOutUserProfilesPortfolioViewFullProfile"
            [username]="(user$ | flAsync)?.username"
          ></app-user-profile-logged-out-link>
        </ng-template>
      </fl-card-header-right>

      <fl-bit
        class="PortfolioCardContent"
        [ngClass]="{ IsCorporate: isCorporate$ | flAsync }"
      >
        <fl-tabs *ngIf="isCorporate$ | flAsync" [flMarginBottom]="Margin.SMALL">
          <fl-tab-item
            flTrackingLabel="CorporateCategoryTab"
            flTrackingReferenceId="AllItems"
            flTrackingReferenceType="CategoryName"
            title="All Items"
            i18n-title="All portfolio items category tab label"
            [selected]="
              (selectedCategory$ | flAsync)?.id === allItemsCategoryItem.id
            "
            (click)="handleSelectedCategoryTab(allItemsCategoryItem)"
          ></fl-tab-item>
          <fl-tab-item
            *ngFor="let tab of categoryTabs$ | flAsync; trackBy: trackById"
            flTrackingLabel="CorporateCategoryTab"
            flTrackingReferenceType="CategoryName"
            [flTrackingReferenceId]="tab.name"
            [selected]="(selectedCategory$ | flAsync)?.id === tab.id"
            [title]="tab.name"
            (click)="handleSelectedCategoryTab(tab)"
          ></fl-tab-item>
        </fl-tabs>

        <app-user-portfolio
          [awardProjectBid$]="awardProjectBid$"
          [awardProjectHireMeState$]="awardProjectHireMeState$"
          [categoriesCollection]="categoriesCollection"
          [categoryId$]="selectedCategoryId$"
          [hireMeUser$]="hireMeUser$"
          [initialItemCount]="PortfolioItemCount.SMALL"
          [isCorporate$]="isCorporate$"
          [isLoggedIn$]="isLoggedIn$"
          [localHourlyRate$]="localHourlyRate$"
          [portfolioItemSize]="PortfolioItemSize.SMALL"
          [reputation$]="userReputation$"
          [showModalOnClick]="true"
          [user$]="user$"
        ></app-user-portfolio>
      </fl-bit>
    </fl-card>
  `,
  styleUrls: ['./user-profile-portfolio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfilePortfolioComponent implements OnInit {
  ButtonColor = ButtonColor;
  Margin = Margin;
  PortfolioItemCount = PortfolioItemCount;
  PortfolioItemSize = PortfolioItemSize;
  trackById = trackById;

  @Input() awardProjectBid$: Rx.Observable<AwardProjectModel>;
  @Input() awardProjectHireMeState$: Rx.Observable<AwardProjectHireMeState>;
  @Input() hireMeUser$: Rx.Observable<HireMeUser>;
  @Input() isCorporate$: Rx.Observable<boolean>;
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;
  @Input() localHourlyRate$: Rx.Observable<LocalHourlyRate | undefined>;
  @Input() profileUserId$: Rx.Observable<number>;
  @Input()
  profileViewUsersDocument: DatastoreDocument<ProfileViewUsersCollection>;
  @Input() skillsCollection: DatastoreCollection<TopSkillsCollection>;
  @Input() userReputation$: Rx.Observable<FreelancerReputation>;

  readonly allItemsCategoryItem: PortfolioCategoryTab = {
    name: 'All Items',
    id: -1,
  };
  private _selectedCategoryTab$ = new Rx.BehaviorSubject<PortfolioCategoryTab>(
    this.allItemsCategoryItem,
  );
  selectedCategory$ = this._selectedCategoryTab$.asObservable();

  categoriesCollection: DatastoreCollection<ProfileCategoriesCollection>;
  categoryTabs$: Rx.Observable<readonly PortfolioCategoryTab[]>;
  selectedCategoryId$: Rx.Observable<number>;
  user$: Rx.Observable<ProfileViewUser>;

  constructor(
    private datastore: Datastore,
    private redirectToPhp: RedirectToPhp,
  ) {}

  ngOnInit() {
    this.selectedCategoryId$ = this.selectedCategory$.pipe(map(cat => cat.id));

    this.user$ = this.profileViewUsersDocument.valueChanges();

    this.categoriesCollection = this.datastore.collection<ProfileCategoriesCollection>(
      'profileCategories',
      query => query.where('userId', '==', this.profileUserId$),
    );

    this.categoryTabs$ = this.categoriesCollection.valueChanges().pipe(
      map(categories =>
        categories
          .filter(category => category.name !== '')
          .map(category => ({
            name: `${category.name} (${category.portfolioItemsCount})`,
            id: category.id,
          })),
      ),
    );
  }

  handleSelectedCategoryTab(tab: PortfolioCategoryTab): void {
    this._selectedCategoryTab$.next(tab);
  }

  handleManagePortfolioClicked(): void {
    // TODO: Remove this when the webapp editable portfolios is functional.
    this.user$
      .pipe(take(1))
      .toPromise()
      .then(user => {
        this.redirectToPhp.doRedirect(`/u/${user.username}?page=portfolio`);
      });
  }
}
